import {Injectable, inject, signal} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {Router} from '@angular/router';
import {AuthService} from './auth.service';

/**
 * Service managing global application context including the active client,
 * selected operating date, client location, and user permissions within that client context.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientInContextService {

  /** BehaviorSubject for client context (backward-compatibility bridge for RxJS consumers). */
  clientInContextSubject = new BehaviorSubject<any>(null);

  /** Signal holding the active client entity and permission flags. */
  clientInContextSignal = signal<any>(null);

  /**
   * Getter for active client context object.
   * @returns Active client data object or null.
   */
  get clientInContext(): any {
    return this.clientInContextSignal();
  }

  /**
   * Setter for active client context object, updating the underlying signal.
   * @param val - Client data object or null.
   */
  set clientInContext(val: any) {
    this.clientInContextSignal.set(val);
  }

  /** BehaviorSubject for selected date context. */
  dateInContextSubject = new BehaviorSubject<Date | null>(null);

  /** Signal holding the active selected Date. */
  dateInContextSignal = signal<Date | null>(null);

  /**
   * Getter for active date in context.
   * @returns The currently selected Date or null.
   */
  get dateInContext(): Date | null {
    return this.dateInContextSignal();
  }

  /**
   * Setter for active date in context, updating the underlying signal.
   * @param val - Selected Date or null.
   */
  set dateInContext(val: Date | null) {
    this.dateInContextSignal.set(val);
  }

  /** BehaviorSubject for active client location context. */
  clientLocSubject = new BehaviorSubject<any>(null);

  /** Signal holding the active client location object. */
  clientLocationInContextSignal = signal<any>(null);

  /**
   * Getter for the current client location.
   * @returns Client location object or null.
   */
  get clientLocationInContext(): any {
    return this.clientLocationInContextSignal();
  }

  /**
   * Setter for the current client location, updating the underlying signal.
   * @param val - Location object or null.
   */
  set clientLocationInContext(val: any) {
    this.clientLocationInContextSignal.set(val);
  }

  /** Signal holding the display name of the selected location. */
  locationNameSignal = signal<string>('');

  private router = inject(Router);
  private authService = inject(AuthService);

  constructor() {
    this.clientInContextSubject.subscribe({
      next: (clientInContextObject) => {
        this.clientInContext = clientInContextObject;
        this.authService.loggedInUserFromAuthService$.subscribe(
          (userDocData) => {
            if (this.clientInContext && userDocData?.clients) {
              const updated = { ...this.clientInContext };
              updated.role = userDocData.clients.filter((client: any) => client?.clientId === clientInContextObject?.id)[0]?.role;
              updated.hasAdminRole = ['admin'].includes(updated?.role);
              updated.hasManagerOrAdminRole = ['admin', 'manager'].includes(updated?.role);
              updated.isDeveloper = userDocData.type === 'developer';
              this.clientInContext = updated;
            }
          }
        );
      }
    });

    this.dateInContextSubject.subscribe({
      next: (dateInContext) => {
        this.dateInContext = dateInContext;
      }
    });

    this.clientLocSubject.subscribe({
      next: (clientLocInContext) => {
        this.clientLocationInContext = clientLocInContext;
      }
    });
  }

  /**
   * Updates the location name signal.
   * @param name - Display name string for the active location.
   */
  setLocationName(name: string): void {
    this.locationNameSignal.set(name);
  }

  /**
   * Retrieves the current location name from its signal.
   * @returns The active location display name string.
   */
  getLocationName(): string {
    return this.locationNameSignal();
  }
}

